import * as React from 'react'
import type { ColumnDef } from '@tanstack/react-table'

import { Button } from '@/components/ui/button'
import { DataTableInfinite } from '@/components/data-table/data-table-infinite'
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header'
import type { DataTableFilterField } from '@/components/data-table/types'
import { DataTableStoreProvider } from '@/lib/store/provider/DataTableStoreProvider'
import { useNuqsAdapter } from '@/lib/store/adapters/nuqs'
import { useFilterState } from '@/lib/store/hooks/useFilterState'
import { createSchema, field } from '@/lib/store/schema'
import { TabRefreshButton } from '@/components/panel/TabRefreshButton'
import { ColorBadge } from '@/components/ColorBadge'

import { useFactCatalogueQuery } from '../hooks/useFactCatalogueQuery'
import { groupCatalogueRows, type CatalogueScopeGroup } from '../catalogueRows'
import { FactDefinition } from './FactDefinition'
import type { CatalogueRow } from '../types'

const TABLE_ID = 'facts-catalogue'

// BYOS schema for the filter rail / URL serialization. Field names match the
// column ids the filters target: fact_name (text), type + source (checkbox
// arrays). The rail shows these keys as the mono hint, so we keep the
// domain-friendly `fact_name` (matching prod) rather than the raw `name`.
const factsSchema = createSchema({
  fact_name: field.string().default(null),
  type: field.array(field.string()).delimiter(',').default([]),
  source: field.array(field.string()).delimiter(',').default([]),
})

const SOURCE_OPTIONS = [
  { label: 'data', value: 'data' },
  { label: 'derived', value: 'derived' },
  { label: 'entered', value: 'entered' },
]

function EmDash() {
  return <span className="text-muted-foreground">—</span>
}

function coverageRatio(coverage: NonNullable<CatalogueRow['coverage']>) {
  if (coverage.subjectCount === 0) {
    return coverage.basis === 'guaranteed' ? 1 : 0
  }
  return coverage.availableCount / coverage.subjectCount
}

const columns: ColumnDef<CatalogueScopeGroup>[] = [
  {
    // id `fact_name` (the filter key shown in the rail); accessor reads
    // `row.name`.
    id: 'fact_name',
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Fact Name" />
    ),
    enableHiding: false,
    enableSorting: false,
    cell: ({ row }) => (
      <span className={row.original.showName ? 'font-mono text-xs' : 'sr-only'}>
        {row.original.name}
      </span>
    ),
  },
  {
    id: 'scope',
    header: 'Scope',
    enableSorting: false,
    cell: ({ row }) => (
      <ul className="max-h-32 w-52 space-y-1 overflow-auto whitespace-normal text-xs">
        {row.original.scopes.map((scope) => (
          <li key={scope.id}>{scope.label}</li>
        ))}
      </ul>
    ),
  },
  {
    id: 'definition',
    header: 'Definition',
    enableSorting: false,
    cell: ({ row }) => (
      <FactDefinition
        name={row.original.name}
        definition={row.original.definition}
      />
    ),
  },
  {
    // Accessor is the `types` array (a fact can carry several); it feeds the
    // rail's faceted counts, while the cell renders the display string.
    id: 'type',
    accessorFn: (row) => row.types,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Type" />
    ),
    enableSorting: false,
    cell: ({ row }) =>
      row.original.type ? <ColorBadge value={row.original.type} /> : <EmDash />,
  },
  {
    accessorKey: 'source',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Source" />
    ),
    enableSorting: false,
    cell: ({ row }) => <ColorBadge value={row.original.source} />,
  },
  {
    id: 'coverage',
    enableSorting: false,
    accessorFn: (row) => (row.coverage ? coverageRatio(row.coverage) : null),
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Coverage" />
    ),
    cell: ({ row }) => {
      const scopes = row.original.scopes
      const sameCoverage = scopes.every(
        (scope) =>
          JSON.stringify(scope.row.coverage) ===
          JSON.stringify(row.original.coverage)
      )
      return (
        <ul className="max-h-32 max-w-64 space-y-1 overflow-auto text-xs">
          {(sameCoverage ? scopes.slice(0, 1) : scopes).map((scope) => {
            const coverage = scope.row.coverage
            return (
              <li key={scope.id}>
                {!sameCoverage && (
                  <span className="block whitespace-normal text-muted-foreground">
                    {scope.label}
                  </span>
                )}
                {coverage ? (
                  <span
                    className="whitespace-nowrap tabular-nums"
                    title={`Coverage basis: ${coverage.basis}`}>
                    {(coverageRatio(coverage) * 100).toFixed(1)}% ·{' '}
                    {coverage.availableCount.toLocaleString()}/
                    {coverage.subjectCount.toLocaleString()}
                  </span>
                ) : (
                  <EmDash />
                )}
              </li>
            )
          })}
        </ul>
      )
    },
  },
  {
    accessorKey: 'description',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Description" />
    ),
    enableSorting: false,
    cell: ({ row }) => {
      const scopes = row.original.scopes
      const sameDescription = scopes.every(
        (scope) => scope.row.description === row.original.description
      )
      return (
        <ul className="max-h-32 max-w-80 space-y-1 overflow-auto whitespace-normal text-muted-foreground">
          {(sameDescription ? scopes.slice(0, 1) : scopes).map((scope) => (
            <li key={scope.id}>
              {!sameDescription && (
                <span className="block text-xs">{scope.label}</span>
              )}
              {scope.row.description || <EmDash />}
            </li>
          ))}
        </ul>
      )
    },
  },
]

function FactCatalogueInner() {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
    dataUpdatedAt,
  } = useFactCatalogueQuery()
  const rows = React.useMemo<CatalogueRow[]>(() => data?.rows ?? [], [data])

  // The catalogue is client-side, but filtering is apply-on-click like the
  // server tables: the rail edits a draft (columnFilters) that only reaches the
  // store when the Search button applies it. We filter the rows by the APPLIED
  // store filters (not the live draft), and pass `manualFiltering` so TanStack
  // doesn't re-filter on the draft on every keystroke.
  const appliedFilters = useFilterState()
  const filteredRows = React.useMemo(() => {
    const nameFilter =
      typeof appliedFilters?.fact_name === 'string'
        ? appliedFilters.fact_name.trim().toLowerCase()
        : ''
    const typeFilter = Array.isArray(appliedFilters?.type)
      ? (appliedFilters.type as string[])
      : []
    const sourceFilter = Array.isArray(appliedFilters?.source)
      ? (appliedFilters.source as string[])
      : []
    if (!nameFilter && typeFilter.length === 0 && sourceFilter.length === 0) {
      return rows
    }
    return rows.filter((row) => {
      if (nameFilter && !row.name.toLowerCase().includes(nameFilter)) {
        return false
      }
      if (
        typeFilter.length > 0 &&
        !typeFilter.some((t) => row.types.includes(t))
      ) {
        return false
      }
      if (sourceFilter.length > 0 && !sourceFilter.includes(row.source)) {
        return false
      }
      return true
    })
  }, [rows, appliedFilters])
  const groupedRows = React.useMemo(
    () => groupCatalogueRows(filteredRows),
    [filteredRows]
  )

  // Type options are the distinct value types present, in first-seen order.
  const typeOptions = React.useMemo(() => {
    const seen = new Set<string>()
    const options: { label: string; value: string }[] = []
    for (const row of rows) {
      for (const type of row.types) {
        if (!seen.has(type)) {
          seen.add(type)
          options.push({ label: type, value: type })
        }
      }
    }
    return options
  }, [rows])

  const filterFields = React.useMemo<
    DataTableFilterField<CatalogueScopeGroup>[]
  >(
    () => [
      {
        label: 'Fact Name',
        // The filter targets the column whose id is `fact_name` (its accessor
        // reads row.name). DataTableFilterField types `value` as keyof TData,
        // but it's really a column id here — cast since `fact_name` isn't a
        // CatalogueRow key. Shown as the rail's mono hint (matches prod).
        value: 'fact_name' as keyof CatalogueScopeGroup,
        type: 'input',
        defaultOpen: true,
      },
      {
        label: 'Type',
        value: 'type',
        type: 'checkbox',
        defaultOpen: true,
        options: typeOptions,
      },
      {
        label: 'Source',
        value: 'source',
        type: 'checkbox',
        defaultOpen: true,
        options: SOURCE_OPTIONS,
      },
    ],
    [typeOptions]
  )

  // The catalogue loads the full list in one fetch — no server pagination.
  const noopFetchNextPage = React.useCallback(() => Promise.resolve(), [])
  const getRowId = React.useCallback((row: CatalogueScopeGroup) => row.id, [])

  const ToolbarActions = React.useMemo(
    () => (
      <TabRefreshButton
        onRefresh={() => void refetch()}
        isFetching={isFetching}
      />
    ),
    [refetch, isFetching]
  )

  if (isError) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4 text-center">
        <p className="text-lg font-semibold text-destructive">
          Failed to load fact catalogue
        </p>
        <p className="text-sm text-muted-foreground">
          {error instanceof Error
            ? error.message
            : 'An unexpected error occurred'}
        </p>
        <Button onClick={() => void refetch()} variant="outline">
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <DataTableInfinite<CatalogueScopeGroup, unknown>
        tableId={TABLE_ID}
        columns={columns}
        data={groupedRows}
        filterFields={filterFields}
        manualFiltering
        manualSorting
        enableRowDetails={false}
        isLoading={isLoading}
        isFetching={isFetching}
        hasNextPage={false}
        fetchNextPage={noopFetchNextPage}
        refetch={refetch}
        totalRows={groupedRows.length}
        totalRowsFetched={groupedRows.length}
        dataUpdatedAt={dataUpdatedAt}
        getRowId={getRowId}
        toolbarActions={ToolbarActions}
        autoColumnWidth
      />
    </div>
  )
}

export function FactCatalogue() {
  const adapter = useNuqsAdapter(factsSchema.definition, {
    id: TABLE_ID,
    history: 'replace',
    // Namespace facts filter params (facts-catalogue.type, …) so they don't
    // collide with Call Logs' `type` filter in the shared query string.
    namespaceUrlKeys: true,
  })

  return (
    <DataTableStoreProvider adapter={adapter}>
      <FactCatalogueInner />
    </DataTableStoreProvider>
  )
}
